import { useBrandFontFamilies } from '@src/hooks/useBrandFontFamilies';
import { useFeatureFlags } from '@src/stores-v2/StoreContext';
import {
  getPopularFontWeights,
  isPopularFontVariantSelected,
} from '@src/utility/fonts';
import { FONTS, JAPANESE_FONTS } from '@src/utility/general';
import { observer } from 'mobx-react-lite';
import { type CSSProperties, type ReactNode, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import ArrowDown from '../../svgs/ArrowDown';
import CheckIcon from '../../svgs/CheckIcon';
import { Chip, ChipGroup } from '../common/ChipGroup';

import type { FontFamily } from '@src/types.ts/story';

export type TextFontPickerTab = 'text' | 'animation' | 'font' | 'template';

type Props = {
  defaultValue: string | undefined;
  fontWeight: string | undefined;
  onSelectFont: (
    name: string,
    weight?: string,
    style?: 'normal' | 'italic',
  ) => void;
  textContent?: ReactNode;
  animationContent?: ReactNode;
  /**
   * Renamed from `styleContent` — what lives here is the caption
   * "Template" picker (Arbor Default, Custom, …), not a Font Style
   * preset. The previous name collided with the new Font Style preset
   * section in Graphics → Text and confused the surface's intent.
   */
  templateContent?: ReactNode;
  /**
   * Fires whenever the active tab changes. Lets the parent render
   * tab-specific sibling content (e.g. hide the "Save Caption Template"
   * button when the user is on the Template tab — see TextSettings).
   */
  onTabChange?: (tab: TextFontPickerTab) => void;
};

const TextFontPicker = observer(
  ({
    defaultValue,
    fontWeight,
    onSelectFont,
    textContent,
    animationContent,
    templateContent,
    onTabChange,
  }: Props) => {
    // Brand families (structured model first; legacy `customFonts[]` fallback),
    // archived entries stripped. Shared resolver — see useBrandFontFamilies.
    const fontFamilies = useBrandFontFamilies();

    // Resolve current font to a custom family (or null for popular fonts)
    const currentFamily = useMemo(
      () =>
        defaultValue
          ? (fontFamilies.find(f =>
              f.variants.some(v => v.postScriptName === defaultValue),
            ) ?? null)
          : null,
      [defaultValue, fontFamilies],
    );

    const currentFontName = useMemo(() => {
      if (!defaultValue) return null;
      if (currentFamily) return currentFamily.name;
      return defaultValue;
    }, [defaultValue, currentFamily]);

    // When the "Hide Arbor Fonts" flag is on, only brand/custom fonts are
    // offered — the curated popular (Arbor) font list is suppressed entirely.
    const { creatorStudioFontsHideArborFonts } = useFeatureFlags();

    const customFamilyNames = useMemo(
      () => new Set(fontFamilies.map(f => f.name)),
      [fontFamilies],
    );

    // Graphics → Text is free-form, so the Japanese fonts are always offered
    // here — not gated on the transcript language the way captions are
    // (PLAT-10420).
    const popularFonts = useMemo(() => {
      if (creatorStudioFontsHideArborFonts) return [];
      return [...FONTS, ...JAPANESE_FONTS].filter(
        name => !customFamilyNames.has(name),
      );
    }, [customFamilyNames, creatorStudioFontsHideArborFonts]);

    const [tab, setTabState] = useState<TextFontPickerTab>('text');
    // Wraps `setTabState` so any subscriber (currently TextSettings, for
    // hiding the Save Caption Template button on the Template tab) sees
    // the change in the same render as the picker itself.
    const setTab = (next: TextFontPickerTab) => {
      setTabState(next);
      onTabChange?.(next);
    };
    const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(() =>
      currentFontName ? new Set([currentFontName]) : new Set(),
    );

    const toggleFamily = (name: string) => {
      setExpandedFamilies(prev => {
        const next = new Set(prev);
        if (next.has(name)) next.delete(name);
        else next.add(name);
        return next;
      });
    };

    const renderFontRow = (name: string, family: FontFamily | undefined) => {
      const isExpanded = expandedFamilies.has(name);
      // Mark the row currently applied to the selected text. For brand
      // families, "current" = the selected element's font_family
      // postScriptName matches any of this family's variants. For popular
      // fonts (no `family`), match on the row's name itself.
      const isCurrent = family
        ? family.variants.some(v => v.postScriptName === defaultValue)
        : name === currentFontName;

      // Render the family header (name + AaBbCc preview) in a representative
      // face of the family so each row previews its own typeface. Brand faces
      // are loaded by useLoadBrandKitFonts keyed by postScriptName; popular
      // fonts resolve by family name. Quote the token so multi-word names
      // ("Special Gothic Expanded One") stay valid CSS.
      const repVariant = family
        ? (family.variants.find(v => !v.archivedAt && v.style === 'normal') ??
          family.variants.find(v => !v.archivedAt))
        : undefined;
      const headerFont: CSSProperties = repVariant
        ? {
            fontFamily: `"${repVariant.postScriptName}", Inter`,
            fontWeight: repVariant.weight,
            fontStyle: repVariant.style,
          }
        : { fontFamily: `"${name}", Inter` };

      return (
        <SFontEntry key={name}>
          <SFontRow isCurrent={isCurrent} onClick={() => toggleFamily(name)}>
            <SChevron isExpanded={isExpanded}>
              <ArrowDown
                width="10"
                height="10"
                strokeColor={isCurrent ? 'var(--accent)' : 'var(--text-muted)'}
              />
            </SChevron>
            <SFontNameLabel style={headerFont}>{name}</SFontNameLabel>
            <SPreview style={headerFont}>AaBbCc</SPreview>
          </SFontRow>

          {isExpanded && (
            <SVariantList>
              {family
                ? family.variants
                    .filter(v => !v.archivedAt)
                    .map(v => {
                      const isSelected = v.postScriptName === defaultValue;
                      return (
                        <SVariantRow
                          key={v.artifactId}
                          isSelected={isSelected}
                          onClick={() =>
                            onSelectFont(
                              v.postScriptName,
                              v.weight.toString(),
                              v.style,
                            )
                          }
                        >
                          <SVariantLabel
                            isSelected={isSelected}
                            style={{
                              fontFamily: `"${v.postScriptName}", Inter`,
                              fontWeight: v.weight,
                              fontStyle: v.style,
                            }}
                          >
                            {v.label}
                          </SVariantLabel>
                          {isSelected && (
                            <CheckIcon
                              width="10"
                              height="10"
                              fillColor="var(--accent)"
                            />
                          )}
                        </SVariantRow>
                      );
                    })
                : getPopularFontWeights(name).map(bv => {
                    const isSelected = isPopularFontVariantSelected(
                      name,
                      currentFontName,
                      bv.fontWeight,
                      fontWeight,
                    );
                    return (
                      <SVariantRow
                        key={bv.fontSubfamily}
                        isSelected={isSelected}
                        onClick={() =>
                          onSelectFont(name, bv.fontWeight.toString())
                        }
                      >
                        <SVariantLabel
                          isSelected={isSelected}
                          style={{
                            // Popular fonts are all loaded via the Google Fonts
                            // import in globals.css, so render each weight row in
                            // its own face. Quote the family for multi-word names.
                            fontFamily: `"${name}", Inter`,
                            fontWeight: bv.fontWeight,
                          }}
                        >
                          {bv.fontSubfamily}
                        </SVariantLabel>
                        {isSelected && (
                          <CheckIcon
                            width="10"
                            height="10"
                            fillColor="var(--accent)"
                          />
                        )}
                      </SVariantRow>
                    );
                  })}
            </SVariantList>
          )}
        </SFontEntry>
      );
    };

    return (
      <SContainer>
        <ChipGroup>
          <Chip isSelected={tab === 'text'} onClick={() => setTab('text')}>
            Text
          </Chip>
          <Chip isSelected={tab === 'font'} onClick={() => setTab('font')}>
            Font
          </Chip>
          <Chip
            isSelected={tab === 'animation'}
            onClick={() => setTab('animation')}
          >
            Animation
          </Chip>
          <Chip
            isSelected={tab === 'template'}
            onClick={() => setTab('template')}
          >
            Template
          </Chip>
        </ChipGroup>

        {tab === 'text' && <STabContent>{textContent}</STabContent>}

        {tab === 'animation' && <STabContent>{animationContent}</STabContent>}

        {tab === 'font' && (
          <SFontList>
            {/* Brand Fonts — the user's uploaded font families. Surfaced
             * first so the kit's typography is the primary choice. */}
            {fontFamilies.length > 0 && (
              <>
                <SSectionLabel>Brand Fonts</SSectionLabel>
                {fontFamilies.map(f => renderFontRow(f.name, f))}
              </>
            )}

            {popularFonts.length > 0 && (
              <>
                <SSectionLabel>Popular Fonts</SSectionLabel>
                {popularFonts.map(name => renderFontRow(name, undefined))}
              </>
            )}
          </SFontList>
        )}

        {tab === 'template' && <STabContent>{templateContent}</STabContent>}
      </SContainer>
    );
  },
);

export default TextFontPicker;

const SContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SFontList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 280px;
  overflow-y: auto;
  scrollbar-color: var(--gray-400) transparent;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--border);
    border-radius: 2px;
  }
`;

const SSectionLabel = styled.span`
  display: block;
  padding: 8px 4px 2px;
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semi-bold);
  letter-spacing: 0.4px;
`;

const SFontEntry = styled.div`
  display: flex;
  flex-direction: column;
`;

const SFontRow = styled.div<{ isCurrent?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 4px;
  border-radius: 6px;
  cursor: pointer;
  ${({ isCurrent }) =>
    isCurrent &&
    css`
      background: color-mix(in srgb, var(--accent) 12%, transparent);
    `}
  &:hover {
    background: ${({ isCurrent }) =>
      isCurrent
        ? 'color-mix(in srgb, var(--accent) 18%, transparent)'
        : 'var(--surface-elevated)'};
  }
`;

const SChevron = styled.div<{ isExpanded: boolean }>`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: ${({ isExpanded }) =>
    isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
  transition: transform 150ms ease;
`;

const SFontNameLabel = styled.span`
  /* Sit immediately next to the preview rather than stretching to the
   * full row width (the previous flex:1 pushed AaBbCc to the right edge).
   * shrink:1 + min-width:0 keeps the ellipsis truncation behaviour for
   * very long names. font-family is applied inline (representative face). */
  flex: 0 1 auto;
  font-size: var(--text-sm);
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const SPreview = styled.span`
  flex-shrink: 0;
  /* font-family / weight applied inline (representative face of the family). */
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

const SVariantList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2px 0 6px;
`;

const SVariantRow = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 32px;
  padding: 0 8px 0 28px;
  border-radius: 6px;
  cursor: pointer;
  background: ${({ isSelected }) =>
    isSelected
      ? 'color-mix(in srgb, var(--accent) 12%, transparent)'
      : 'transparent'};
  &:hover {
    background: ${({ isSelected }) =>
      isSelected
        ? 'color-mix(in srgb, var(--accent) 18%, transparent)'
        : 'var(--surface-elevated)'};
  }
`;

const SVariantLabel = styled.span<{ isSelected: boolean }>`
  font-size: var(--text-sm);
  /* font-family / weight / style are applied inline per variant so each row
     previews its own loaded face (postScriptName), width, and slant. */
  color: ${({ isSelected }) =>
    isSelected ? 'var(--accent)' : 'var(--text-secondary)'};
`;

const STabContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 4px;
`;
